import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Trophy, Heart, RotateCcw, 
  Volume2, VolumeX, Pause, Play, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const JumpHeroGame = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  
  // Game state
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [level, setLevel] = useState(1);
  
  // Session state
  const [session, setSession] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [endingGame, setEndingGame] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  // Canvas dimensions - responsive
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 300 });
  
  // Game objects ref
  const gameState = useRef({
    player: { x: 50, y: 250, vy: 0, width: 25, height: 35, isJumping: false },
    platforms: [],
    coins: [],
    obstacles: [],
    gravity: 0.5,
    jumpForce: -11,
    scrollSpeed: 3,
    groundY: 260
  });

  // Update canvas size based on screen
  useEffect(() => {
    const updateSize = () => {
      const maxWidth = Math.min(window.innerWidth - 32, 600);
      const height = Math.min(maxWidth * 0.6, 350);
      setCanvasSize({ width: maxWidth, height });
      
      // Update game state ground level
      gameState.current.groundY = height - 40;
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Load session on mount
  useEffect(() => {
    const stored = localStorage.getItem('currentGameSession');
    if (stored) {
      const sessionData = JSON.parse(stored);
      if (sessionData.gameSlug === 'jump-hero') {
        setSession(sessionData);
        setLives(sessionData.lives === -1 ? 999 : sessionData.lives);
      } else {
        navigate('/games');
      }
    } else {
      navigate('/games');
    }
  }, [navigate]);

  // Timer
  useEffect(() => {
    let timer;
    if (gameStarted && !gameOver && !isPaused) {
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameStarted, gameOver, isPaused]);

  // Initialize game
  const initGame = useCallback(() => {
    const state = gameState.current;
    const { width, height } = canvasSize;
    state.groundY = height - 40;
    state.player = { x: 50, y: state.groundY - 35, vy: 0, width: 25, height: 35, isJumping: false };
    state.platforms = [
      { x: 0, y: state.groundY, width: width, height: 50 },
      { x: width * 0.25, y: height - 100, width: 80, height: 15 },
      { x: width * 0.5, y: height - 150, width: 80, height: 15 },
      { x: width * 0.75, y: height - 200, width: 80, height: 15 },
    ];
    state.coins = [
      { x: width * 0.28, y: height - 130, collected: false },
      { x: width * 0.54, y: height - 180, collected: false },
      { x: width * 0.78, y: height - 230, collected: false },
    ];
    state.obstacles = [
      { x: width * 0.4, y: state.groundY - 20, width: 25, height: 18 },
      { x: width * 0.65, y: state.groundY - 20, width: 25, height: 18 },
    ];
    setScore(0);
    setGameOver(false);
    setGameStarted(true);
  }, [canvasSize]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver || isPaused) return;
      
      const state = gameState.current;
      if ((e.code === 'Space' || e.code === 'ArrowUp') && !state.player.isJumping) {
        state.player.vy = state.jumpForce;
        state.player.isJumping = true;
      }
      if (e.code === 'ArrowLeft') {
        state.player.x = Math.max(0, state.player.x - 8);
      }
      if (e.code === 'ArrowRight') {
        state.player.x = Math.min(canvasSize.width - 30, state.player.x + 8);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameOver, isPaused, canvasSize.width]);

  // Game loop
  useEffect(() => {
    if (!gameStarted || gameOver || isPaused) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = gameState.current;
    const { width, height } = canvasSize;

    const gameLoop = () => {
      // Clear canvas
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, width, height);

      // Update player physics
      state.player.vy += state.gravity;
      state.player.y += state.player.vy;

      // Platform collision
      let onPlatform = false;
      state.platforms.forEach(platform => {
        if (
          state.player.x + state.player.width > platform.x &&
          state.player.x < platform.x + platform.width &&
          state.player.y + state.player.height > platform.y &&
          state.player.y + state.player.height < platform.y + platform.height + 10 &&
          state.player.vy >= 0
        ) {
          state.player.y = platform.y - state.player.height;
          state.player.vy = 0;
          state.player.isJumping = false;
          onPlatform = true;
        }
      });

      // Ground collision
      if (state.player.y + state.player.height > state.groundY) {
        state.player.y = state.groundY - state.player.height;
        state.player.vy = 0;
        state.player.isJumping = false;
      }

      // Coin collection
      state.coins.forEach((coin, idx) => {
        if (!coin.collected) {
          const dx = (state.player.x + state.player.width/2) - coin.x;
          const dy = (state.player.y + state.player.height/2) - coin.y;
          if (Math.sqrt(dx*dx + dy*dy) < 22) {
            coin.collected = true;
            setScore(prev => prev + 10);
          }
        }
      });

      // Obstacle collision
      state.obstacles.forEach(obstacle => {
        if (
          state.player.x + state.player.width > obstacle.x &&
          state.player.x < obstacle.x + obstacle.width &&
          state.player.y + state.player.height > obstacle.y
        ) {
          // Hit obstacle
          setLives(prev => {
            const newLives = prev - 1;
            if (newLives <= 0 && session?.difficulty !== 'basic') {
              setGameOver(true);
            } else {
              // Reset player position
              state.player.x = 50;
              state.player.y = state.groundY - 35;
            }
            return newLives;
          });
        }
      });

      // Draw platforms
      ctx.fillStyle = '#4a5568';
      state.platforms.forEach(platform => {
        ctx.fillRect(platform.x, platform.y, platform.width, platform.height);
        // Platform top decoration
        ctx.fillStyle = '#68d391';
        ctx.fillRect(platform.x, platform.y, platform.width, 4);
        ctx.fillStyle = '#4a5568';
      });

      // Draw coins
      state.coins.forEach(coin => {
        if (!coin.collected) {
          ctx.beginPath();
          ctx.arc(coin.x, coin.y, 10, 0, Math.PI * 2);
          ctx.fillStyle = '#fbbf24';
          ctx.fill();
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 2;
          ctx.stroke();
          // Coin shine
          ctx.beginPath();
          ctx.arc(coin.x - 2, coin.y - 2, 3, 0, Math.PI * 2);
          ctx.fillStyle = '#fef3c7';
          ctx.fill();
        }
      });

      // Draw obstacles (spikes)
      ctx.fillStyle = '#ef4444';
      state.obstacles.forEach(obstacle => {
        ctx.beginPath();
        ctx.moveTo(obstacle.x, obstacle.y + obstacle.height);
        ctx.lineTo(obstacle.x + obstacle.width / 2, obstacle.y);
        ctx.lineTo(obstacle.x + obstacle.width, obstacle.y + obstacle.height);
        ctx.closePath();
        ctx.fill();
      });

      // Draw player
      ctx.fillStyle = '#8b5cf6';
      ctx.fillRect(state.player.x, state.player.y, state.player.width, state.player.height);
      // Player face
      ctx.fillStyle = '#fff';
      ctx.fillRect(state.player.x + 6, state.player.y + 8, 4, 4);
      ctx.fillRect(state.player.x + 14, state.player.y + 8, 4, 4);
      ctx.fillRect(state.player.x + 8, state.player.y + 18, 8, 3);

      // Check win condition (all coins collected)
      if (state.coins.every(c => c.collected)) {
        setLevel(prev => prev + 1);
        // Generate new level
        state.coins = [
          { x: width * 0.28 + Math.random() * 50, y: height - 130 - level * 8, collected: false },
          { x: width * 0.54 + Math.random() * 50, y: height - 180 - level * 8, collected: false },
          { x: width * 0.78 + Math.random() * 50, y: height - 230 - level * 8, collected: false },
        ];
        setScore(prev => prev + 50); // Level bonus
      }

      // Continue game loop
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, isPaused, level, session, canvasSize]);

  // End game
  const endGame = async (won) => {
    if (!session || endingGame) return;
    
    setEndingGame(true);

    try {
      const res = await api.post('/games/jump-hero/end', {
        session_id: session.sessionId,
        score,
        won,
        time_played_seconds: timeElapsed
      });

      setResult(res.data);
      localStorage.removeItem('currentGameSession');
      
      toast({
        title: won ? "Great Job!" : "Game Over",
        description: won && res.data.reward_earned > 0 
          ? `You earned ${res.data.reward_earned} AGP!` 
          : res.data.message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to end game",
        variant: "destructive"
      });
    } finally {
      setEndingGame(false);
    }
  };

  // Handle game over
  useEffect(() => {
    if (gameOver && !result) {
      endGame(score > 100);
    }
  }, [gameOver, result, score]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Game Over!</h2>
            <p className="text-muted-foreground mb-4">Level {level} reached</p>

            <div className="space-y-3 my-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Score</span>
                <span className="font-bold">{score}</span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Time</span>
                <span className="font-bold">{formatTime(timeElapsed)}</span>
              </div>
              {result.reward_earned > 0 && (
                <div className="flex justify-between p-3 bg-yellow-500/20 rounded-lg">
                  <span>Reward</span>
                  <span className="font-bold text-yellow-400">+{result.reward_earned} AGP</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/games')}>
                Game Zone
              </Button>
              <Button className="flex-1" onClick={() => navigate('/games')}>
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Start screen
  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="text-6xl mb-4">🦘</div>
            <h2 className="text-2xl font-bold mb-2">Jump Hero</h2>
            <p className="text-muted-foreground mb-6">
              Collect coins, avoid spikes, reach new levels!
            </p>
            
            <div className="bg-muted p-4 rounded-lg mb-6 text-left">
              <h3 className="font-bold mb-2">Controls:</h3>
              <p className="text-sm text-muted-foreground">
                ⬆️ / Space - Jump<br/>
                ⬅️ ➡️ - Move left/right
              </p>
            </div>

            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-400" />
                <span>{lives} Lives</span>
              </div>
              <div className="text-sm text-muted-foreground capitalize">
                {session?.difficulty || 'advance'}
              </div>
            </div>

            <Button className="w-full" size="lg" onClick={initGame}>
              <Play className="w-5 h-5 mr-2" />
              Start Game
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="jump-hero-game">
      {/* HUD */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => {
              if (confirm('Exit game?')) {
                endGame(score > 100);
              }
            }}>
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exit</span>
            </Button>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                <span className="font-bold">{score}</span>
              </div>

              {session?.difficulty !== 'basic' && (
                <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                  <span className="font-bold">{lives}</span>
                </div>
              )}

              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                <span className="font-mono">{formatTime(timeElapsed)}</span>
              </div>

              <div className="bg-purple-500/20 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <span className="text-purple-400 font-bold">Lv.{level}</span>
              </div>
            </div>

            <Button variant="ghost" size="sm" onClick={() => setIsPaused(!isPaused)}>
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Game Canvas */}
      <div className="max-w-xl mx-auto p-2 sm:p-4">
        <Card className="overflow-hidden">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="w-full"
            style={{ imageRendering: 'pixelated' }}
          />
        </Card>

        {isPaused && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-20">
            <Card className="p-6 text-center">
              <h2 className="text-xl font-bold mb-4">Paused</h2>
              <Button onClick={() => setIsPaused(false)}>
                <Play className="w-4 h-4 mr-2" />
                Resume
              </Button>
            </Card>
          </div>
        )}

        {/* Mobile Controls - Always visible on touch devices */}
        <div className="mt-4 flex justify-center gap-3">
          <Button 
            size="lg"
            className="w-16 h-16 sm:w-20 sm:h-20 text-2xl"
            onTouchStart={(e) => { e.preventDefault(); gameState.current.player.x = Math.max(0, gameState.current.player.x - 25); }}
            onClick={() => gameState.current.player.x = Math.max(0, gameState.current.player.x - 25)}
          >
            ⬅️
          </Button>
          <Button 
            size="lg"
            className="w-16 h-16 sm:w-20 sm:h-20 text-2xl"
            onTouchStart={(e) => {
              e.preventDefault();
              if (!gameState.current.player.isJumping) {
                gameState.current.player.vy = gameState.current.jumpForce;
                gameState.current.player.isJumping = true;
              }
            }}
            onClick={() => {
              if (!gameState.current.player.isJumping) {
                gameState.current.player.vy = gameState.current.jumpForce;
                gameState.current.player.isJumping = true;
              }
            }}
          >
            ⬆️
          </Button>
          <Button 
            size="lg"
            className="w-16 h-16 sm:w-20 sm:h-20 text-2xl"
            onTouchStart={(e) => { e.preventDefault(); gameState.current.player.x = Math.min(canvasSize.width - 30, gameState.current.player.x + 25); }}
            onClick={() => gameState.current.player.x = Math.min(canvasSize.width - 30, gameState.current.player.x + 25)}
          >
            ➡️
          </Button>
        </div>

        {/* Instructions */}
        <p className="text-center text-xs text-muted-foreground mt-3">
          Tap buttons or use Arrow keys / Space to play
        </p>
      </div>
    </div>
  );
};

export default JumpHeroGame;
